import React, {Key, useEffect, useState} from 'react';
import {wdiCore, WdiModalAppProps, WdiModalAppResult, WdiModalProps} from '@/components/WdiControls';
import {Button, DatePicker, Input, message, Popover, TableColumnsType, Typography} from 'antd';
import {WdiModal} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {FilterColumn, GridFilterForm} from '@/components/GridWidget/GridFilterForm';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import {TablePaginationConfig} from "antd/lib/table";
import {SorterResult} from "antd/lib/table/interface";
import {CloseOutlined} from '@ant-design/icons';
import {OA} from "@/services/oa";
import moment from 'moment';

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any
};


export type OaProgramSelectProps = {
  value?: API.OaProgramVo[];
  onChange?: (value?: API.OaProgramVo[]) => void;
};

export const OaProgramSelectApp: React.FC<OaProgramSelectProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const onClose = () => {
    setModalProps({visible: false, title: ""});
  };
  const onSave = (result?: WdiModalAppResult) => {
    setModalProps({visible: false, title: ""});
    if (result && result.code === ResponseStatus.SUCCESS) {
      props.onChange?.(result.data);
    }
  };

  const newProps: any = {
    onClick: () => {
      setModalProps({
        visible: true,
        title: "选择OA单号",
        data: {
          records: props.value,
        },
      });
    },
    allowClear: {
      clearIcon: (
        <Button
          shape='circle'
          style={{border: "none", width: 20, height: 20}}
          icon={<CloseOutlined/>}
          size="small"
          onClick={() => props.onChange?.([])}
        />
      )
    }
  };

  return (
    <>
      <Popover content={props.value?.map(item => wdiCore.String.Join(item.documentNumber, item.note)).join()}>
        <Input
          {...newProps}
          placeholder={"请选择OA单号"}
          value={props.value?.map(item => wdiCore.String.Join(item.documentNumber, item.note)).join()}
        />
      </Popover>
      {
        modalProps.visible &&
        <OaProgramSelectModalApp
          {...modalProps}
          onClose={onClose}
          onSave={onSave}
        />
      }
    </>
  );
}

const OaProgramSelectModalApp: React.FC<WdiModalAppProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<API.OaProgramVo[]>([]);
  const [selectedRowsState, setSelectedRowsState] = useState<{
    version: number,
    rows: Map<Key, API.OaProgramVo>
  }>({version: 0, rows: new Map<Key, API.OaProgramVo>()});
  const [tableColumns, setTableColumns] = useState<any[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
    },
    sorter: null,
    filterOptions: null,
  });

  useEffect(() => {
    (async () => {
      setTableColumns(await initTableColumns())
    })()
  }, []);

  useEffect(() => {
    const records: API.OaProgramVo[] = props.data?.records ?? [];
    records.forEach((item) => {
      addSelectedRowState(item);
    })
  }, [props.data?.records])

  const addSelectedRowState = (row: API.OaProgramVo) => {
    if (!row.documentNumber) return;
    const rowMap = selectedRowsState.rows;
    rowMap.clear();
    rowMap.set(row.documentNumber, row);
    setSelectedRowsState({version: selectedRowsState.version + 1, rows: rowMap})
  }

  const delSelectedRowState = (row: API.OaProgramVo) => {
    if (!row.documentNumber) return;
    const rowMap = selectedRowsState.rows;
    rowMap.delete(row.documentNumber);
    setSelectedRowsState({version: selectedRowsState.version + 1, rows: rowMap})
  }

  //OA申请单号（文本框），OA主题（文本框），OA申请日期（起止）
  const filterColumns: FilterColumn[] = [
    {
      title: 'OA申请单号',
      dataIndex: 'documentNumber',
      span: 4,
      render: () => {
        return <Input placeholder={"请输入OA申请单号"}/>;
      }
    },
    {
      title: 'OA主题',
      dataIndex: 'note',
      span: 4,
      render: () => {
        return <Input placeholder={"请输入OA主题"}/>;
      },
    },
    {
      title: 'OA申请日期',
      dataIndex: 'dateFrom',
      span: 8,
      render: () => {
        return <DatePicker.RangePicker style={{width: '100%'}}/>
      }
    },
  ];
  const initTableColumns = async () => {
    const columns: TableColumnsType<API.OaProgramVo> = [
      {
        title: 'OA申请单号',
        dataIndex: 'documentNumber',
        ellipsis: true,
        width: 150,
        render: (_, record) => (
          <Typography.Link onClick={() => {
            window.open(record.url);
          }}>{record.documentNumber}</Typography.Link>
        )
      },
      {
        title: 'OA主题',
        width: 200,
        dataIndex: 'note',
        ellipsis: true,
      },
      {
        title: '流程类型',
        width: 150,
        dataIndex: 'oaFlowTypeDesc',
        ellipsis: true,
      },
      {
        title: '金额',
        dataIndex: 'amount',
        ellipsis: true,
        render: (_, record) => {
          return wdiCore.Number.AmountToString(record.amount)
        },
      },
      {
        title: '申请金额',
        width: 150,
        dataIndex: 'amount',
        ellipsis: true,
      },
      {
        title: '申请人',
        width: 150,
        dataIndex: 'employeeName',
        ellipsis: true,
        render: (_, record) => {
          return wdiCore.String.CodeOrName(record.employeeCode, record.employeeName);
        },
      },
      {
        title: '申请日期',
        dataIndex: 'documentDate',
        ellipsis: true,
      },
    ];
    return columns
  }

  const getFilterOptions = (params: TableParams) => {
    const filterOptions: any[] = [];
    if (params.filterOptions) {
      const {documentNumber, note, dateFrom} = params.filterOptions;
      if (documentNumber) {
        filterOptions.push({
          title: 'OA申请单号',
          field: 'documentNumber',
          opt: 7,
          value: documentNumber
        })
      }
      if (note) {
        filterOptions.push({
          title: 'OA主题',
          field: 'note',
          opt: 7,
          value: note
        })
      }
      if (dateFrom?.[0]) {
        filterOptions.push({
          title: 'OA申请日期',
          field: 'dateFrom',
          opt: 4,
          value: moment(dateFrom[0]).format('YYYY-MM-DD')
        })
      }
      if (dateFrom?.[1]) {
        filterOptions.push({
          title: 'OA申请日期',
          field: 'dateFrom',
          opt: 6,
          value: moment(dateFrom[1]).format('YYYY-MM-DD')
        })
      }
      wdiCore.Object.RemoveNullOrUndefined(filterOptions);
    }
    return filterOptions;
  }

  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions = getFilterOptions(params);
    const req: any = {
      filterOptions: JSON.stringify(filterOptions),
      id: 'OaProgramSelect',
      pagenum: params.pagination.current ? (params.pagination.current - 1) : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
      sortdatafield: 'documentNumber',
      sortorder: 'DESC'
    };
    OA.queryProgram(req).then(res => {
      if (res?.code != ResponseStatus.SUCCESS && res?.message) {
        message.error(res.message)
      }
      setDataSource(res?.data?.records ?? []);
      setTableParams({
        pagination: {
          ...params.pagination,
          total: res?.data?.totalCount ?? 0,
        },
        sorter: params.sorter,
        filterOptions: params.filterOptions
      });
      setLoading(false);
    });
  };
  const doSearch = (filterOptions: any) => {
    const {documentNumber, note} = filterOptions;
    if (!documentNumber && !note) {
      message.error("申请单号/主题要至少输入一个");
      return
    }
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  };

  const onSelectChange = (record: API.OaProgramVo, selected: boolean) => {
    if (!record.documentNumber) return;
    if (selected) {
      addSelectedRowState(record)
      return;
    }
    delSelectedRowState(record)
  };

  const doSave = () => {
    const rows = Array.from(selectedRowsState.rows.values());
    if (!rows || rows.length == 0) {
      message.error('请至少选择一条数据');
      return false;
    }
    if (!rows || rows.length > 1) {
      message.error('只能选择一个OA申请单');
      return false;
    }
    props.onSave?.({code: ResponseStatus.SUCCESS, data: rows});
    return true;
  };

  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      bodyStyle={{height: 600}}
      onCancel={() => {
        props.onClose?.()
      }}
      onOk={doSave}
    >
      <GridFilterForm doSearch={doSearch} columns={filterColumns}/>
      <WdiTable
        rowSelection={{
          selectedRowKeys: Array.from(selectedRowsState.rows.keys()),
          onSelect: onSelectChange,
          // onSelectAll: onSelectAllChange
        }}
        columns={tableColumns}
        showPageRows={10}
        rowKey="documentNumber"
        dataSource={dataSource}
        pagination={{
          ...tableParams.pagination,
          onChange: (page, pageSize) => {
            fetchData(Object.assign({}, tableParams, {
              pagination: {
                ...tableParams.pagination,
                current: page,
                pageSize: pageSize
              }
            }))
          }
        }}
        loading={loading}
      />
    </WdiModal>
  );
};

